/**
 * ComptabiliteParametres — Configuration cabinet + auto-envoi + catégories.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings as SettingsIcon, Save, Mail, TestTube, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ComptabiliteParametres() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    try { const r = await api.get("/accounting/settings"); setS(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!s.expert_email) { toast.error("Email expert-comptable requis"); return; }
    setSaving(true);
    try {
      const payload = { ...s, cc_emails: typeof s.cc_emails === "string"
        ? s.cc_emails.split(",").map(x => x.trim()).filter(Boolean)
        : (s.cc_emails || []) };
      await api.put("/accounting/settings", payload);
      toast.success("Paramètres enregistrés"); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const doTestSend = async () => {
    if (!testEmail) { toast.error("Email requis"); return; }
    try { await api.post("/accounting/test-send", { to_email: testEmail });
      toast.success(`Email de test envoyé à ${testEmail}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <div className="p-6">Chargement...</div>;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6 max-w-3xl" data-testid="ct-settings-page">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
        <SettingsIcon className="w-6 h-6 text-blue-600" />Paramètres Comptabilité
      </h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" />Destinataire (expert-comptable)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Nom expert-comptable</Label>
            <Input value={s.expert_name} onChange={(e) => set("expert_name", e.target.value)} />
          </div>
          <div><Label>Email expert-comptable *</Label>
            <Input type="email" value={s.expert_email} onChange={(e) => set("expert_email", e.target.value)} data-testid="ct-expert-email" />
          </div>
          <div><Label>CC (séparés par virgule)</Label>
            <Input value={Array.isArray(s.cc_emails) ? s.cc_emails.join(", ") : s.cc_emails} onChange={(e) => set("cc_emails", e.target.value)} />
          </div>
          <div><Label>Nom expéditeur</Label>
            <Input value={s.sender_name} onChange={(e) => set("sender_name", e.target.value)} />
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label>Email de test</Label>
              <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="votre@email.fr" data-testid="ct-test-email" />
            </div>
            <Button variant="outline" onClick={doTestSend} data-testid="ct-test-send-btn"><TestTube className="w-4 h-4 mr-1" />Test</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Envoi automatique mensuel</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={s.auto_send_enabled} onCheckedChange={(v) => set("auto_send_enabled", v)} data-testid="ct-auto-send" />
            <div className="flex-1"><Label>Envoyer automatiquement chaque mois</Label>
              <p className="text-xs text-slate-500">Le cron `accounting-daily` envoie au jour/heure configurés</p>
            </div>
          </div>
          {s.auto_send_enabled && (
            <div className="grid grid-cols-2 gap-3 pl-8">
              <div><Label>Jour du mois (1-28)</Label>
                <Input type="number" min="1" max="28" value={s.auto_send_day} onChange={(e) => set("auto_send_day", parseInt(e.target.value) || 5)} />
              </div>
              <div><Label>Heure (HH:MM UTC)</Label>
                <Input value={s.auto_send_time} onChange={(e) => set("auto_send_time", e.target.value)} placeholder="09:00" />
              </div>
              <div className="col-span-2 flex items-center gap-3">
                <Switch checked={s.send_even_if_incomplete} onCheckedChange={(v) => set("send_even_if_incomplete", v)} />
                <Label>Envoyer même si dossier incomplet</Label>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Catégories incluses</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {[["include_sales", "Factures de vente"],
              ["include_purchases", "Factures d'achat"],
              ["include_bank", "Relevés bancaires"],
              ["include_sepa", "Remises SEPA"],
              ["include_payroll", "Bulletins de paie"]].map(([k, l]) => (
            <div key={k} className="flex items-center gap-3">
              <Switch checked={s[k]} onCheckedChange={(v) => set(k, v)} />
              <Label>{l}</Label>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Message par défaut</CardTitle></CardHeader>
        <CardContent>
          <Textarea rows={6} value={s.custom_message} onChange={(e) => set("custom_message", e.target.value)} />
          <p className="text-xs text-slate-500 mt-1">Tokens disponibles : <code>{"{period_label}"}</code>, <code>{"{tenant_name}"}</code></p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ct-save-btn">
          <Save className="w-4 h-4 mr-1.5" />Enregistrer
        </Button>
      </div>

      <FecExportCard />
    </div>
  );
}

function FecExportCard() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/accounting/fec/preview?period_start=${year}-01-01&period_end=${year}-12-31`);
      setPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const downloadFec = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/accounting/fec/export?period_start=${year}-01-01&period_end=${year}-12-31`, { responseType: "blob" });
      const filename = preview?.filename || `FEC_${year}.txt`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`FEC ${year} téléchargé`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="fec-export-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" />Export FEC (Fichier des Écritures Comptables)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Format DGFIP conforme à l'<strong>article A47 A-1 du LPF</strong> — obligatoire lors d'un contrôle fiscal.
          Agrège vos ventes, achats et notes de frais en fichier TSV standardisé.
        </p>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <Label>Exercice fiscal</Label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} min="2020" max={new Date().getFullYear()} data-testid="fec-year-input" />
          </div>
          <Button variant="outline" onClick={runPreview} disabled={loading} data-testid="fec-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Prévisualiser
          </Button>
          <Button onClick={downloadFec} disabled={downloading || !preview?.ready} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fec-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Télécharger
          </Button>
        </div>
        {preview && (
          <div className="bg-white rounded-lg border p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Fichier</span><code className="text-slate-900 font-mono">{preview.filename}</code></div>
            <div className="flex justify-between"><span className="text-slate-500">SIREN</span><code className="text-slate-900 font-mono">{preview.siren}</code></div>
            <div className="flex justify-between"><span className="text-slate-500">Ventes</span><strong>{preview.sales_count}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Achats</span><strong>{preview.purchases_count}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Notes de frais</span><strong>{preview.expenses_count}</strong></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="text-slate-500">Lignes FEC générées</span><strong className="text-blue-700">{preview.lines}</strong></div>
            {!preview.ready && (
              <p className="text-xs text-orange-700 mt-2 flex items-center gap-1">
                ⚠ SIREN manquant sur le tenant — renseignez-le dans les paramètres entreprise avant l'export officiel.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
